/**
 * Modale « En savoir plus » d'un abonnement (= screens/Prices/components/ContractCard.tsx, même
 * contenu : CGV administrables du BO, sinon le texte de repli de la v1 à l'identique, puis le lien
 * PDF). Variante Chakra `mmv2` (thème v2) : la modale est rendue dans un portail, hors de
 * #mindbody-app, donc stylée par le thème et non par les feuilles v2.
 */
import {
  Box,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import type { MergedPricingContract } from 'screens/Prices/utils/mergePricingService';
import { withoutEmptyParagraphs } from './lib';

export function ContractTermsModal({ merged, onClose }: { merged: MergedPricingContract | null; onClose: () => void }) {
  const engagementCount = merged?.contract.NumberOfAutopays ?? null;
  const strong = { as: 'span' as const, fontWeight: 600, color: 'mmv2.ink' };

  return (
    <Modal
      isOpen={!!merged}
      onClose={onClose}
      isCentered
      // Carte centrée même en mobile (maquette `.modal`), plutôt que le plein écran de la v1.
      size="lg"
      scrollBehavior="inside"
      variant="mmv2"
    >
      <ModalOverlay />
      <ModalContent className="mmv2-contract-terms" sx={{ '&, & *': { textTransform: 'none' } }}>
        <ModalHeader sx={{ textTransform: 'uppercase !important' }}>
          {merged?.title || 'Détails de l’abonnement'}
        </ModalHeader>
        <ModalCloseButton aria-label="Fermer" />
        {/* Fondu en bas de la zone qui défile : la dernière ligne visible n'est plus coupée net. */}
        {/*
         * Zone qui défile atteignable au clavier (WCAG 2.1.1) : dans Safari, sans tabIndex, ni Tab ni les flèches ne la
         * faisaient défiler (59 px de CGV inaccessibles à 390, revue du 05/10). Contour du site au focus clavier.
         */}
        <ModalBody
          pb={8}
          tabIndex={0}
          role="region"
          aria-label="Conditions de l’abonnement"
          sx={{
            maskImage: 'linear-gradient(to bottom, #000 calc(100% - 28px), transparent)',
            '&:focus': { outline: 'none' },
            '&:focus-visible': { outline: '2.5px solid', outlineColor: 'mmv2.focus', outlineOffset: '-2.5px' },
          }}
        >
          {merged?.contractTerms ? (
            <Box
              fontSize="14px"
              color="mmv2.ink2"
              lineHeight="1.6"
              sx={{
                '& p': { mb: 3 },
                '& p:last-child': { mb: 0 },
                '& strong, & b': { color: 'mmv2.ink', fontWeight: 600 },
                '& a': { textDecoration: 'underline' },
                '& ul, & ol': { pl: 5, mb: 3 },
              }}
              // CGV saisies dans le BO (administrateurs), rendues comme en v1.
              dangerouslySetInnerHTML={{ __html: withoutEmptyParagraphs(merged.contractTerms) }}
            />
          ) : merged ? (
            <Stack spacing={3} fontSize="14px" color="mmv2.ink2" lineHeight="1.6">
              <Text>
                <Text {...strong}>🔁 Report des sessions :</Text> les sessions incluses dans votre abonnement sont
                valables uniquement sur le mois en cours et ne sont pas reportables sur le mois suivant.
              </Text>
              <Text>
                <Text {...strong}>📅 Durée d’engagement :</Text>{' '}
                {engagementCount
                  ? `durée d’engagement minimum de ${engagementCount} mois à compter de la date de souscription.`
                  : 'durée d’engagement minimum à compter de la date de souscription.'}
              </Text>
              <Text>
                <Text {...strong}>🔄 Reconduction tacite :</Text> à l’issue de la période d’engagement, l’abonnement est
                reconduit tacitement pour une durée illimitée par périodes successives d’un mois entier.
              </Text>
              <Text>
                <Text {...strong}>🚀 Date de début d’abonnement :</Text> votre abonnement débute le jour de la
                souscription, avec un premier paiement effectué le jour même.
              </Text>
              <Text>
                <Text {...strong}>🎟️ Validité des crédits :</Text> profitez de vos crédits dans l’ensemble des studios
                Dynamo, RIISE et Punch, y compris Reformer et Lagree.
              </Text>
              {merged.contractPdfUrl ? (
                <Text>
                  <Text {...strong}>📜 CGV complémentaires :</Text> pour plus de détails, consultez nos{' '}
                  <Link href={merged.contractPdfUrl} isExternal textDecoration="underline" color="inherit">
                    Conditions Générales de Vente
                  </Link>
                  .
                </Text>
              ) : null}
            </Stack>
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
